import { useState } from 'react';
import { Routes, Route, Link, NavLink } from 'react-router-dom';
import { SignedIn, SignedOut, SignInButton, UserButton } from '@clerk/clerk-react';
import { Menu, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useMe } from './lib/me';
import DemoToggle from './components/DemoToggle';
import IndexPage from './pages/index';
import ListPage from './pages/list';
import MapPage from './pages/map';
import CheckoutPage from './pages/checkout';
import OrdersPage from './pages/orders';
import SellerApply from './pages/seller/apply';
import SellerDashboard from './pages/seller/dashboard';
import SellerListings from './pages/seller/listings';
import SellerSeatBlocks from './pages/seller/seat-blocks';
import SellerSales from './pages/seller/sales';
import AdminDashboard from './pages/admin/dashboard';
import AdminApplications from './pages/admin/applications';
import AdminSeatBlocks from './pages/admin/seat-blocks';
import AdminGames from './pages/admin/games';
import AdminDisputes from './pages/admin/disputes';
import AdminListings from './pages/admin/listings';
import AdminSellers from './pages/admin/sellers';
import TermsPage from './pages/legal/terms';
import PrivacyPage from './pages/legal/privacy';
import BuyerProtectionPage from './pages/legal/buyer-protection';

/** Gate a route to admins. Non-admins see a friendly notice rather than data. */
function RequireAdmin({ children }: { children: ReactNode }) {
  const { me, loading } = useMe();
  if (loading) return <p className="muted">Loading…</p>;
  if (me?.role !== 'admin') return <p className="muted">This area is for administrators only.</p>;
  return <>{children}</>;
}

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `text-sm font-semibold no-underline transition-colors ${
    isActive ? 'text-white' : 'text-white/60 hover:text-white'
  }`;

const mobileLinkClass = ({ isActive }: { isActive: boolean }) =>
  `block py-1.5 text-sm font-medium no-underline ${isActive ? 'text-white' : 'text-white/70'}`;

export default function App() {
  const { me } = useMe();
  const isAdmin = me?.role === 'admin';
  const [menuOpen, setMenuOpen] = useState(false);

  const links = (
    <>
      <NavLink to="/list" className={navLinkClass} onClick={() => setMenuOpen(false)}>
        Browse
      </NavLink>
      <NavLink to="/map" className={navLinkClass} onClick={() => setMenuOpen(false)}>
        Map
      </NavLink>
      <SignedIn>
        <NavLink to="/orders" className={navLinkClass} onClick={() => setMenuOpen(false)}>
          My orders
        </NavLink>
        <NavLink to="/seller/dashboard" className={navLinkClass} onClick={() => setMenuOpen(false)}>
          Sell
        </NavLink>
        {isAdmin && (
          <NavLink to="/admin" className={navLinkClass} onClick={() => setMenuOpen(false)}>
            Admin
          </NavLink>
        )}
      </SignedIn>
    </>
  );

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-giants-blue sticky top-0 z-50">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14 gap-4">
            <div className="flex items-center gap-8 min-w-0">
              <Link
                to="/"
                className="font-display text-2xl text-white tracking-[0.04em] no-underline shrink-0"
              >
                NYGTIX
              </Link>
              <nav className="hidden md:flex items-center gap-6">{links}</nav>
            </div>
            <div className="flex items-center gap-3">
              <DemoToggle />
              <SignedOut>
                <SignInButton mode="modal">
                  <button className="hidden md:block bg-transparent border border-white/30 text-white hover:bg-white/10 px-4 py-1.5 rounded-md text-sm font-semibold">
                    Sign in
                  </button>
                </SignInButton>
              </SignedOut>
              <SignedIn>
                <UserButton afterSignOutUrl="/" />
              </SignedIn>
              <Link
                to="/seller/dashboard"
                className="hidden md:inline-block bg-giants-red hover:bg-giants-red-dark text-white px-4 py-1.5 rounded-md text-sm font-semibold no-underline transition-colors"
              >
                List tickets
              </Link>
              <button
                className="md:hidden bg-transparent hover:bg-white/10 text-white p-1.5"
                onClick={() => setMenuOpen((v) => !v)}
                aria-label="Menu"
              >
                {menuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
        </div>
        {menuOpen && (
          <div className="md:hidden bg-giants-blue-dark border-t border-white/10 px-4 py-3">
            {links}
            <div className="flex gap-3 pt-3 mt-2 border-t border-white/10">
              <SignedOut>
                <SignInButton mode="modal">
                  <button className="flex-1 bg-transparent border border-white/30 text-white py-2 rounded-md text-sm font-semibold">
                    Sign in
                  </button>
                </SignInButton>
              </SignedOut>
              <Link
                to="/seller/dashboard"
                onClick={() => setMenuOpen(false)}
                className="flex-1 text-center bg-giants-red text-white py-2 rounded-md text-sm font-semibold no-underline"
              >
                List tickets
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 w-full max-w-[1200px] mx-auto px-4 sm:px-6 py-8">
        <Routes>
          <Route path="/" element={<IndexPage />} />
          <Route path="/list" element={<ListPage />} />
          <Route path="/map" element={<MapPage />} />
          <Route path="/checkout/:listingId" element={<CheckoutPage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/orders/:orderId" element={<OrdersPage />} />
          <Route path="/seller/apply" element={<SellerApply />} />
          <Route path="/seller/dashboard" element={<SellerDashboard />} />
          <Route path="/seller/listings" element={<SellerListings />} />
          <Route path="/seller/seat-blocks" element={<SellerSeatBlocks />} />
          <Route path="/seller/sales" element={<SellerSales />} />
          <Route path="/admin" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
          <Route path="/admin/applications" element={<RequireAdmin><AdminApplications /></RequireAdmin>} />
          <Route path="/admin/seat-blocks" element={<RequireAdmin><AdminSeatBlocks /></RequireAdmin>} />
          <Route path="/admin/listings" element={<RequireAdmin><AdminListings /></RequireAdmin>} />
          <Route path="/admin/sellers" element={<RequireAdmin><AdminSellers /></RequireAdmin>} />
          <Route path="/admin/games" element={<RequireAdmin><AdminGames /></RequireAdmin>} />
          <Route path="/admin/disputes" element={<RequireAdmin><AdminDisputes /></RequireAdmin>} />
          <Route path="/legal/terms" element={<TermsPage />} />
          <Route path="/legal/privacy" element={<PrivacyPage />} />
          <Route path="/legal/buyer-protection" element={<BuyerProtectionPage />} />
        </Routes>
      </main>

      <footer className="bg-giants-blue-dark py-6 mt-12">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="text-white/40 text-xs">
            NYGTIX · MetLife Stadium · Independent — not affiliated with the New York Giants or
            NFL
          </span>
          <nav className="flex items-center gap-4">
            <Link to="/legal/buyer-protection" className="text-white/50 hover:text-white/80 text-xs no-underline transition-colors">
              Buyer protection
            </Link>
            <Link to="/legal/terms" className="text-white/50 hover:text-white/80 text-xs no-underline transition-colors">
              Terms
            </Link>
            <Link to="/legal/privacy" className="text-white/50 hover:text-white/80 text-xs no-underline transition-colors">
              Privacy
            </Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
